"use client";

import { PSYPACT_MAP_URL, PSYPACT_STATES } from "@klarify/constants";
import { Trans } from "@lingui/react/macro";
import { useState } from "react";
import { cn } from "../../lib/utils";
import { Badge, badgeVariants } from "../ui/badge";
import { Button } from "../ui/button";
import { telehealthRegionLabels } from "./format-location";

const COLLAPSED_COUNT = 6;

export type TelehealthRegionsProps = {
	regions: { country: string; state: string | null }[];
	// Primary/operating jurisdiction. `regions` holds only the non-primary
	// (telehealth) rows. When `includePrimary` is set, the primary region is
	// shown first in the list AND counts toward PsyPact coverage; otherwise it
	// is ignored for both.
	operatingCountry?: string | null;
	operatingState?: string | null;
	// True when the profile serves its primary region remotely (telehealth-only
	// or offers video/phone). Set by the template from session formats.
	includePrimary?: boolean;
};

export const isPsypactSuperset = (
	regions: { country: string; state: string | null }[],
	primary?: { country: string | null; state: string | null } | null,
): boolean => {
	const usStateCodes = new Set(
		regions
			.filter((r) => r.country === "US" && r.state !== null)
			.map((r) => r.state as string),
	);
	if (primary?.country === "US" && primary.state) {
		usStateCodes.add(primary.state);
	}
	return PSYPACT_STATES.every((code) => usStateCodes.has(code));
};

export const TelehealthRegions = ({
	regions,
	operatingCountry,
	operatingState,
	includePrimary,
}: TelehealthRegionsProps) => {
	const [expanded, setExpanded] = useState(false);
	const primary = includePrimary
		? { country: operatingCountry ?? null, state: operatingState ?? null }
		: null;
	const labels = telehealthRegionLabels(regions, primary);
	if (labels.length === 0) {
		return null;
	}
	const visible = expanded ? labels : labels.slice(0, COLLAPSED_COUNT);
	const hiddenCount = labels.length - visible.length;
	const showPsypactNote = isPsypactSuperset(regions, primary);
	return (
		<div>
			<p className="font-medium text-klarify-neutral-500 text-xs uppercase tracking-wider">
				<Trans>Licensed or authorized in</Trans>
			</p>
			<ul className="mt-2 flex flex-wrap gap-1.5">
				{visible.map((label, index) => (
					<li key={`${label}-${index}`}>
						<Badge variant="pillMuted">{label}</Badge>
					</li>
				))}
				{expanded || hiddenCount > 0 ? (
					<li>
						{/* Reuse the pill badge sizing for parity with the location
						    badges; `h-auto` drops the Button's fixed height so it sits
						    flush with the pills. */}
						<Button
							type="button"
							variant="unstyled"
							className={cn(
								badgeVariants({ variant: "pillSuccess" }),
								"h-auto shadow-none hover:bg-klarify-forest-100",
							)}
							onClick={() => setExpanded((v) => !v)}
						>
							{expanded ? (
								<Trans>Show less</Trans>
							) : (
								<Trans>+{{ count: hiddenCount }} more</Trans>
							)}
						</Button>
					</li>
				) : null}
			</ul>
			{showPsypactNote && (
				<p className="mt-2 text-klarify-neutral-500 text-xs">
					<Trans>Authorized in additional states through PsyPact.</Trans>{" "}
					<a
						href={PSYPACT_MAP_URL}
						target="_blank"
						rel="noopener noreferrer"
						className="text-klarify-forest-700 underline underline-offset-2 hover:text-klarify-forest-900"
					>
						<Trans>View the PsyPact map</Trans>
					</a>
				</p>
			)}
		</div>
	);
};
TelehealthRegions.displayName = "TelehealthRegions";
